import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem,
  FancyDropdownToggle
} from 'dpl/components/FancyDropdown';
import Icon from 'dpl/common/components/Icon';
import { FILTER_PRICE } from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { LEARNING_CENTER_PRICE_PATH } from 'dpl/shared/constants/urls';
import { beginsWithVowel } from 'dpl/shared/utils';
import SmartLink from 'dpl/components/SmartLink';

const DEFAULT_PRICE_OPTION = { value: null, title: 'Any' };

export default function PriceFilter() {
  const queryParams = useSelector(state => state.queryParams);
  const { puppyPriceRanges, breedName, medianBreedPrice } = useSelector(
    ({ server }) => server
  );
  const dispatch = useDispatch();

  const priceOptions = [...puppyPriceRanges, DEFAULT_PRICE_OPTION];

  const priceOption = useMemo(
    () =>
      priceOptions.find(({ value }) => value === queryParams[FILTER_PRICE]) ||
      DEFAULT_PRICE_OPTION,
    [queryParams[FILTER_PRICE]]
  );

  function handleFilterChange(option) {
    const newPriceOption = option.value;

    if (newPriceOption === null) {
      dispatch(resetParams(FILTER_PRICE, { hash: true }));
    } else {
      dispatch(pushParams({ [FILTER_PRICE]: newPriceOption }, { hash: true }));
    }
  }

  const [priceDropdownIsOpen, setPriceDropdownIsOpen] = useState(false);
  return (
    <>
      <p className="mb4 font-16 fw-medium md:font-18 md:font-serif">Price</p>
      <FancyDropdown
        isOpen={priceDropdownIsOpen}
        onChange={setPriceDropdownIsOpen}
      >
        {/*
          Calculating average price can be inaccurate due to pricing outliers. Using a
          calculated median price based off breed's litter price range low/high values instead.
        */}
        {Boolean(medianBreedPrice) && (
          <p className="f2 mb4 lh-copy">
            The average price of {beginsWithVowel(breedName) ? 'an' : 'a'}{' '}
            {breedName} puppy is {medianBreedPrice}.{' '}
            <SmartLink
              className="underline"
              to={LEARNING_CENTER_PRICE_PATH}
              target="_blank"
            >
              Learn more
            </SmartLink>
          </p>
        )}
        <FancyDropdownToggle>
          <div className="w-100 ba b--light-gray br3 pv2 ph3 mb1 flex justify-between align-items-center">
            {priceOption.title}
            <Icon name={`fat-arrow-${priceDropdownIsOpen ? 'up' : 'down'}`} />
          </div>
        </FancyDropdownToggle>

        <FancyDropdownMenu className="overflow-y-auto ba b--silver br3">
          {priceOptions.map((option, idx) => {
            return (
              <FancyDropdownMenuItem key={idx} itemKey={idx}>
                <button
                  data-test-id={`price_option_${idx}`}
                  type="button"
                  onClick={() => handleFilterChange(option)}
                  className="pv2 ph2 w-100"
                >
                  {option.title}
                </button>
              </FancyDropdownMenuItem>
            );
          })}
        </FancyDropdownMenu>
      </FancyDropdown>
    </>
  );
}
